/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * Validations utility for table and column names, display names, and descriptions.
 * Follows PostgreSQL naming standards and application-specific rules.
 */

const PG_COL_NAME_REGEX = /^[a-z_][a-z0-9_]*$/;
const PG_MAX_COLNAME_LEN = 63;
const PG_RESERVED_KEYWORDS = new Set([
    "user", "table", "select", "insert", "update", "delete", "where", "from", "order", "group", "by",
    "having", "create", "drop", "alter", "schema", "database", "grant", "revoke"
]);

/**
 * Checks if a name is a valid PostgreSQL column/table name.
 * @param {string} name 
 * @returns {boolean}
 */
export function isPgNameValid(name) {
    if (!name) return false;
    if (name.length > PG_MAX_COLNAME_LEN) return false;
    if (!PG_COL_NAME_REGEX.test(name)) return false;
    if (PG_RESERVED_KEYWORDS.has(name)) return false;
    return true;
}

/** 
 * Checks if a display name contains only alphabets, numbers, and spaces.
 * @param {string} name 
 * @returns {boolean}
 */
export function isDisplayNameValid(name) {
    return /^[a-zA-Z0-9 ]+$/.test(name || "");
}

/**
 * Checks a description: prose with punctuation is fine, markup is not.
 * @param {string} desc 
 * @returns {boolean}
 */
export function isDescriptionValid(desc) {
    // A description is prose, so ordinary punctuation has to be allowed: packs ship
    // descriptions with commas, brackets and full stops, and the old letters-and-spaces
    // rule made every pack-installed table impossible to save without rewriting it first.
    // Angle brackets stay out so a description can never carry markup.
    return !/[<>]/.test(desc || "");
}

/**
 * Checks if a string is a valid Regular Expression.
 * @param {string} str 
 * @returns {boolean}
 */
export function isValidRegex(str) {
    try {
        if (str && str.trim() !== "") new RegExp(str);
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Deep cleans an object of empty strings, nulls, and undefined values.
 * @param {object} obj 
 * @returns {object}
 */
export function cleanObject(obj) {
    const out = {};
    Object.entries(obj).forEach(([k, v]) => {
        if (
            v !== undefined &&
            v !== null &&
            !(typeof v === "string" && v.trim() === "") &&
            !(typeof v === "object" && Object.keys(v).length === 0)
        ) {
            out[k] = v;
        }
    });
    return out;
}
